import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  CampaignResponse,
  CampaignStatus,
  CreativePreviewResponse,
  Flight,
  FlightFrequency,
  FlightImpressions,
  CreativeUploadResponse,
  Zone,
  ContentCategory
} from '../types/campaign'
import { zoneAbbrs, DATE_STRING, PACIFIC_TIMEZONE } from '../types/constants'

dayjs.extend(utc)
dayjs.extend(timezone)

export const formatCurrency = (amount: number) => {
  return Math.round(amount).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
  })
}

export const formatNumber = (amount: number) => {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 0
  })
}

export const getTotalBudget = (campaign: CampaignResponse) => {
  const budget = campaign.flights.reduce((acc, flight) => {
    const flightBudget = flight.delivery.budget ?? 0
    return acc + Number(flightBudget)
  }, 0)

  return formatCurrency(budget)
}

export const getTargetImpressions = (campaign: CampaignResponse) => {
  const impressions = campaign.flights.reduce((acc, flight) => {
    const flightImpressions =
      (flight.delivery as FlightImpressions).impression_target ?? 0
    return acc + Number(flightImpressions)
  }, 0)

  return formatNumber(impressions)
}

export const getPlayFrequency = (campaign: CampaignResponse) => {
  const playFrequency = campaign.flights.reduce((acc, flight) => {
    const flightPlayFrequency =
      (flight.delivery as FlightFrequency).play_frequency_seconds ?? 0
    return acc + Number(flightPlayFrequency)
  }, 0)

  return formatNumber(playFrequency)
}

export const getCPM = (campaign: CampaignResponse) => {
  let totalImpressions = 0
  let totalBudget = 0
  campaign.flights.forEach((flight) => {
    if (flight.delivery.type === 'impressions') {
      totalBudget += Number(flight.delivery.budget) || 0
      totalImpressions += Number(flight.delivery.impression_target)
    }
  })

  const campaignCPM = (totalBudget / totalImpressions) * 1000
  return formatCurrency(campaignCPM)
}

/**
 * Convert a Pacific date string (YYYY-MM-DD HH:mm:ss) to UTC start-of-day for backend.
 * Frontend uses Pacific; backend stores UTC.
 */
export const pacificDateStringToUtcStartOfDay = (dateString: string): string =>
  dayjs.tz(dateString, DATE_STRING, PACIFIC_TIMEZONE).startOf('day').utc().format()

/**
 * Convert a Pacific date string to UTC end-of-day for backend.
 * Pacific end of day (e.g. 2/28 23:59:59) becomes UTC; the UTC date may be the next calendar day (e.g. 3/1 07:59:59), which is correct.
 */
export const pacificDateStringToUtcEndOfDay = (dateString: string): string =>
  dayjs.tz(dateString, DATE_STRING, PACIFIC_TIMEZONE).endOf('day').utc().format()

export const formatDate = (date: string | null, timeZone?: string) => {
  if (!date) {
    return '-'
  }
  return timeZone
    ? dayjs(date).tz(timeZone).format('MMM D, YYYY')
    : dayjs(date).format('MMM D, YYYY')
}

export const formatTime = (date: string | null, timeZone: string) => {
  if (!date) {
    return '-'
  }
  const isDST = dayjs().tz(timeZone).format('z') !== zoneAbbrs[timeZone][0]
  const abbr = zoneAbbrs[timeZone][isDST ? 1 : 0]
  return `${dayjs(date).tz(timeZone).format('h:mm A')} (${abbr})`
}

// Status calculation helpers
export const getFlightStatus = (flight: Flight): CampaignStatus => {
  const { starts_at, ends_at } = flight.duration
  const now = dayjs()
  if (!starts_at || !ends_at) {
    return CampaignStatus.Draft
  }
  const start = dayjs(starts_at).tz(flight.time_zone)
  const end = dayjs(ends_at).tz(flight.time_zone)
  if (now.isBefore(start)) {
    return CampaignStatus.Scheduled
  }
  if (now.isAfter(end)) {
    return CampaignStatus.Completed
  }
  return CampaignStatus.Live
}

export const getCreativeStatus = (
  creative: CreativeUploadResponse,
  flight: Flight,
  campaignStatus: CampaignStatus
): CampaignStatus => {
  // Parent overrides
  if (campaignStatus === CampaignStatus.Draft) return CampaignStatus.Draft
  if (flight.status === CampaignStatus.Draft) {
    return CampaignStatus.Draft
  }

  // Use creative calculated_duration; fallback to flight duration when null
  const starts_at = creative.calculated_duration.starts_at || flight.duration.starts_at
  const ends_at = creative.calculated_duration.ends_at || flight.duration.ends_at

  // If we still cannot determine a range, default to Draft
  if (!starts_at || !ends_at) return CampaignStatus.Draft

  const start = dayjs(starts_at).tz(flight.time_zone)
  const end = dayjs(ends_at).tz(flight.time_zone)
  const now = dayjs()

  // If campaign is Scheduled, creatives should display Scheduled regardless of dates
  if (campaignStatus === CampaignStatus.Scheduled) {
    return CampaignStatus.Scheduled
  }
  if (flight.status ===
      CampaignStatus.Scheduled &&
    now.isBefore(start)
  ) {
    return CampaignStatus.Scheduled
  }

  if (now.isBefore(start)) return CampaignStatus.Scheduled
  if (now.isAfter(end)) return CampaignStatus.Completed
  return CampaignStatus.Live
}

export const formatPreviewInput = (
  selectedPlacements: number[],
  selectedStores: number[],
  availableZones: Zone[]
) => {
  const input = {
    stores: selectedStores.map((store) => ({ id: store })),
    placements: selectedPlacements.map((placement) => ({ id: placement })),
    zones: [] as { id: number }[]
  }
  availableZones.forEach((zone) => {
    if (zone.placements.every((p) => selectedPlacements.includes(p.id))) {
      // A zone is only part of the input if all of its placements are selected
      input.zones.push({ id: zone.id })
      // Zone inclusion already accounts for its placements, so we remove these from the input
      input.placements = input.placements.filter((placement) => {
        return !zone.placements.some((p) => p.id === placement.id)
      })
    }
  })
  return input
}

// Available formats + any additional formats from saved creatives
export const getFormatsToRender = (
  creativeFormats: CreativePreviewResponse,
  flight: Flight
) => {
  if (!creativeFormats) return []

  const currentFormats = creativeFormats.aspect_ratios.map((format) => ({
    ...format,
    isOutdated: false
  }))

  if (flight.creatives.length === 0) {
    return currentFormats
  }

  const savedOrientations = Array.from(
    new Set(
      flight.creatives
        .map((creative) => {
          const asset =
            creative.assets.find((creativeAsset) => creativeAsset.type === 'video') ?? creative.assets[0]
          return asset?.aspect_ratio.orientation
        })
        .filter((orientation: string) => Boolean(orientation))
    )
  )

  const outdatedFormats = savedOrientations
    .filter(
      (orientation) =>
        !creativeFormats.aspect_ratios.some(
          (format) => format.orientation === orientation
        )
    )
    .map((orientation) => {
      const sampleCreative = flight.creatives.find((creative) => {
        const asset =
          creative.assets.find((creativeAsset) => creativeAsset.type === 'video') ?? creative.assets[0]
        return asset?.aspect_ratio.orientation === orientation
      })
      const asset =
        sampleCreative?.assets.find((creativeAsset) => creativeAsset.type === 'video') ??
        sampleCreative?.assets[0]

      return {
        ratio: asset?.aspect_ratio.ratio || '',
        orientation,
        max_creative_duration_ms: 30000,
        minimum_resolution: {
          width: asset?.width || 1920,
          height: asset?.height || 1080
        },
        isOutdated: true
      }
    })

  return [...currentFormats, ...outdatedFormats]
}

const DEFAULT_UNTITLED_NAME = 'Untitled Creative'

export const stripFileExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf('.')
  if (lastDotIndex <= 0) return fileName
  return fileName.substring(0, lastDotIndex)
}

export const sanitizeCreativeName = (name: string, maxLength = 100) => {
  // Remove invalid characters, keep alphanumeric, space, hyphen, underscore, parentheses
  const cleaned = name
    .replace(/\s+/g, ' ')
    .replace(/[^a-zA-Z0-9 \-_()]/g, '')
    .trim()

  if (!cleaned) return DEFAULT_UNTITLED_NAME

  return cleaned.length > maxLength ? cleaned.slice(0, maxLength).trim() : cleaned
}

export const deriveCreativeNameFromFile = (
  fileName: string,
  maxLength = 100
) => {
  const withoutExt = stripFileExtension(fileName)
  return sanitizeCreativeName(withoutExt, maxLength)
}

export const ensureUniqueCreativeName = (
  baseName: string,
  existingNames: string[],
  maxLength = 100
) => {
  const normalizedBase = sanitizeCreativeName(baseName, maxLength)
  // Count how many names already match the base or the pattern `${base} (n)`
  const pattern = new RegExp(`^${normalizedBase.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}(?: \\((\\d+)\\))?$`)

  let maxSuffix = 0
  let hasExact = false
  existingNames.forEach((name) => {
    const match = name.match(pattern)
    if (match) {
      if (match[1]) {
        const n = parseInt(match[1], 10)
        if (!Number.isNaN(n)) maxSuffix = Math.max(maxSuffix, n)
      } else {
        hasExact = true
      }
    }
  })

  if (!hasExact) {
    // unique as-is
    return normalizedBase
  }

  const next = maxSuffix + 1
  const candidate = `${normalizedBase} (${next})`
  return candidate.length > maxLength
    ? candidate.slice(0, maxLength)
    : candidate
}

// Content Category Utilities
export const getParentCategoryId = (category: ContentCategory | null | undefined): number | null => {
  if (!category) return null
  return category.parent_id ?? category.id
}

export const getParentCategory = (
  category: ContentCategory | null | undefined,
  allCategories: ContentCategory[] | undefined
): ContentCategory | null => {
  if (!category || !allCategories) return null

  const parentId = getParentCategoryId(category)
  if (parentId === null) return null

  return allCategories.find(c => c.id === parentId) || null
}

export const getParentCategoryName = (
  category: ContentCategory | null | undefined,
  allCategories: ContentCategory[] | undefined
): string => {
  const parent = getParentCategory(category, allCategories)
  return parent?.name || 'Uncategorized'
}
